import { anchorBelowElement, Menu } from '@/components/ui/Menu.web';
import { queryDatabase } from '@/lib/databases/api';
import { resolveBlockTitles } from '@/lib/databases/relation-titles';
import {
  encodeRelationCellValue,
  parseRelationIds,
  relationTargetDatabaseId,
} from '@/lib/databases/values';
import { createElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Pressable } from 'react-native';

import type { BlockRecord } from '@/lib/blocks/types';
import type { BlockPropertyRecord } from '@/lib/databases/types';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import { SelectChip } from './SelectChip.web';

function payloadRelationIds(property: BlockPropertyRecord | undefined): string[] {
  const v = property?.value;
  if (!v || typeof v !== 'object' || !('t' in v)) {
    return [];
  }
  return parseRelationIds(v as { t: string; v?: unknown });
}

type RelationCellEditorProps = {
  column: DatabaseColumnDefinition;
  property: BlockPropertyRecord | undefined;
  disabled?: boolean;
  saving?: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: (value: Record<string, unknown> | null) => Promise<void>;
  variant?: 'table' | 'panel';
};

export function RelationCellEditor({
  column,
  property,
  disabled,
  saving,
  isOpen,
  onOpen,
  onClose,
  onSave,
  variant = 'table',
}: RelationCellEditorProps) {
  const targetDbId = relationTargetDatabaseId(column.config);
  const selectedIds = payloadRelationIds(property);
  const [query, setQuery] = useState('');
  const [titles, setTitles] = useState<Map<string, string>>(new Map());
  const [candidates, setCandidates] = useState<BlockRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const panelMode = variant === 'panel';

  function setRootEl(el: HTMLDivElement | null) {
    rootRef.current = el;
    setAnchorEl(el);
  }

  useLayoutEffect(() => {
    if (isOpen && rootRef.current) {
      setAnchorEl(rootRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedIds.length === 0) {
      setTitles(new Map());
      return;
    }
    let cancelled = false;
    void resolveBlockTitles(selectedIds).then((map) => {
      if (cancelled) {
        return;
      }
      const next = new Map<string, string>();
      for (const id of selectedIds) {
        const hit = map.get(id);
        if (hit && !hit.deleted) {
          next.set(id, hit.title);
        }
      }
      setTitles(next);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedIds.join('\0'), property?.syncVersion]);

  useEffect(() => {
    if (!targetDbId || !isOpen) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    void queryDatabase(targetDbId, {
      search: query.trim() || undefined,
      limit: 30,
      includeProperties: false,
    })
      .then((result) => {
        if (!cancelled) {
          setCandidates(result.rows);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCandidates([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [targetDbId, query, isOpen]);

  async function commitIds(nextIds: string[]) {
    const encoded = encodeRelationCellValue(nextIds);
    await onSave(encoded as Record<string, unknown> | null);
  }

  async function toggleRow(rowId: string) {
    if (disabled || saving) {
      return;
    }
    const has = selectedIds.includes(rowId);
    const next = has ? selectedIds.filter((id) => id !== rowId) : [...selectedIds, rowId];
    await commitIds(next);
  }

  function toggleOpen() {
    if (disabled || saving || !targetDbId) {
      return;
    }
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }

  const chips = selectedIds
    .map((id) => {
      const label = titles.get(id);
      if (!label) {
        return null;
      }
      return { id, label };
    })
    .filter((c): c is { id: string; label: string } => c !== null);

  const popoverContent =
    !targetDbId
      ? createElement(
          'p',
          { className: 'cs-db-relation-hint' },
          'Choose a target database in Edit property.',
        )
      : isOpen
        ? [
            createElement('input', {
              key: 'search',
              type: 'text',
              className: 'cs-db-select-search',
              placeholder: 'Link to a row…',
              value: query,
              autoFocus: true,
              disabled: disabled || saving,
              onChange: (e: Event) => setQuery((e.target as HTMLInputElement).value),
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                  onClose();
                }
                e.stopPropagation();
              },
            }),
            createElement(
              'div',
              { key: 'list', className: 'cs-db-select-options' },
              loading
                ? createElement('p', { className: 'cs-db-relation-hint' }, 'Loading…')
                : candidates.length === 0
                  ? createElement('p', { className: 'cs-db-relation-hint' }, 'No rows found')
                  : candidates.map((row) =>
                      createElement(
                        'button',
                        {
                          key: row.id,
                          type: 'button',
                          className: `cs-db-select-option${
                            selectedIds.includes(row.id)
                              ? ' cs-db-select-option-selected'
                              : ''
                          }`,
                          disabled: disabled || saving,
                          onClick: () => void toggleRow(row.id),
                        },
                        row.title?.trim() || 'Untitled',
                      ),
                    ),
              selectedIds.length > 0
                ? createElement(
                    'button',
                    {
                      key: 'clear',
                      type: 'button',
                      className: 'cs-db-select-clear',
                      disabled: disabled || saving,
                      onClick: () => void commitIds([]),
                    },
                    'Clear all',
                  )
                : null,
            ),
          ]
        : null;

  const popoverInner =
    popoverContent &&
    createElement(
      'div',
      { className: 'cs-db-select-popover' },
      ...(Array.isArray(popoverContent) ? popoverContent : [popoverContent]),
    );

  const tableMenu =
    !panelMode &&
    createElement(Menu, {
      open: isOpen && !!targetDbId,
      anchor: anchorEl ? anchorBelowElement(anchorEl) : null,
      onClose,
      dataAttribute: 'cs-relation-editor',
      minWidth: 260,
      children: isOpen ? popoverInner : null,
    });

  const cellContent = createElement(
    'div',
    {
      className: panelMode
        ? 'cs-db-select-cell cs-db-select-cell-panel'
        : 'cs-db-select-cell cs-db-select-cell--table',
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        if (!panelMode) {
          toggleOpen();
        }
      },
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
    },
    chips.length > 0
      ? chips.map((c) =>
          createElement(SelectChip, {
            key: c.id,
            label: c.label,
            color: 'gray',
            onRemove:
              !disabled && isOpen
                ? () => {
                    void commitIds(selectedIds.filter((id) => id !== c.id));
                  }
                : undefined,
          }),
        )
      : !isOpen
        ? createElement('span', { className: 'cs-db-select-placeholder' }, 'Link rows…')
        : null,
    panelMode && isOpen ? popoverInner : null,
  );

  if (panelMode) {
    return createElement(
      'div',
      {
        ref: setRootEl,
        'data-cs-relation-editor': 'true',
        className: 'cs-db-relation-editor-root',
      },
      cellContent,
    );
  }

  return createElement(
    'div',
    {
      ref: setRootEl,
      'data-cs-relation-editor': 'true',
      className: 'cs-db-relation-editor-root',
    },
    createElement(
      Pressable,
      {
        onPress: toggleOpen,
        className:
          'flex h-full min-h-[32px] w-full min-w-0 max-w-full overflow-hidden items-center justify-start px-2 py-1',
      },
      cellContent,
    ),
    tableMenu,
  );
}
